/**
 * Class description
 *
 * Created: 2019-08-13
 * @author jonas
 */

import {ParsedGameData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedGameData";
import {ParsedServerData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedServerData";
import {IBetLineWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {Timeline, TimelineLite} from "gsap";
import {SymbolUtil} from "../../utils/SymbolUtil";
import {StateMatrix} from "../../utils/StateMatrix";
import {ISymbolStateConfig, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {BetLineEvent} from "@nolimitcity/slot-game/bin/core/betline/event/BetLineEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameRoundPresentation} from "@nolimitcity/slot-game/bin/timelinegameround/core/gameround/GameRoundPresentation";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {ImmortalFruitsGameConfig} from "../../ImmortalFruitsGameConfig";
import {ImmortalFruitsGameModuleConfig} from "../../ImmortalFruitsGameModuleConfig";
import {ImmortalFruitsParsedGameData} from "../../server/data/ImmortalFruitsParsedGameData";
import {ImmortalFruitsGameMode} from "../../ImmortalFruitsGameMode";
import {ForegroundAnimation} from "../../foregroundanimation/ForegroundAnimation";
import {bigWinEvents} from "../../views/WinTextView";
import {BalanceEvent} from "@nolimitcity/slot-game/bin/core/balance/event/BalanceEvent";
import {SoundManager} from "../../resource/sound/SoundManager";
import {ImmortalFruits} from "../../ImmortalFruits";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";

export class InitialWin implements GameRoundPresentation {
    private _gameData: ParsedGameData;
    private _symbolStates: StateMatrix<string>;
    private _tl: TimelineLite;
    private winType: string;
    private winConfig: any;
    private isNewRoundStarted: boolean = false;

    constructor() {
        this.addEventListeners();
    }

    /**
     * @inheritDoc
     * @param data
     */
    public prepare(data: ParsedServerData): void {
        if (data instanceof ImmortalFruitsParsedGameData) {
            ImmortalFruitsGameModuleConfig.countUpView.calculateTextScale(0, data.singleWin);
            this.winType = data!.currentWinRatioConfig!.type || "win";
            this.winConfig = data!.currentWinRatioConfig!;
            // #305
            //ImmortalFruitsGameConfig.instance.WIN_BELOW_STAKE = (ImmortalFruits.api.gameClientConfiguration.belowStakeWinRestriction && (data.singleWin <= data.playedBetValue));
            ImmortalFruitsGameConfig.instance.WIN_BELOW_STAKE = ImmortalFruitsGameModuleConfig.featurePrice.isWinBelowStake();
        }
        this._gameData = <ParsedGameData>data;
        this._symbolStates = SymbolUtil.createSymbolStateMatrixFromBetWins(
            <IBetLineWinData[]>this._gameData.betWins!,
            ImmortalFruitsGameConfig.instance.SYMBOL_STATES.noWin!.keyword,
            ImmortalFruitsGameConfig.instance.SYMBOL_STATES.win!.keyword);
    }

    /**
     * @inheritDoc
     */
    public getAnimation(): TimelineLite | null {
        if (this._gameData.singleWin <= 0) {
            return null;
        }
        this._tl = new TimelineLite();
        const noWinAnimations: TimelineLite = this.getNoWinSymbolAnimations();
        const winAnimations: TimelineLite = this.getWinSymbolAnimations();

        this._tl.add(() => {
            noWinAnimations;
            winAnimations;
            EventHandler.dispatchEvent(new GameEvent(bigWinEvents.PLAY_GREENLIGHTS, this.winType));
            if (this.winConfig!.sound && !this._gameData!.isBigWin && !ImmortalFruitsGameConfig.instance.WIN_BELOW_STAKE) {// #305
                SoundManager.playSound(this.winConfig!.sound);
            }
            if (this.winType == "medium" || this.winType == "large") {
                EventHandler.dispatchEvent(new GameEvent(bigWinEvents.PLAY_BIGWIN, this.winType))
            }

            // #320
            StageManager.getLayer(ImmortalFruitsGameConfig.instance.LAYERS.INITIAL_WIN_AMOUNT_BET_LINES.name).removeChildren();
            if (!ImmortalFruitsGameConfig.instance.WIN_BELOW_STAKE) {
                ImmortalFruitsGameModuleConfig.winTextView.showBetlineAmountInitialWin(this._gameData!.betWins);
            }
        });
        this._tl.add(() => {
            if (this.winType == "medium" || this.winType == "large") {
                //SoundManager.playMediumAndLargeWinSound();
            }
            this._gameData!.isBigWin && (this._gameData.mode != ImmortalFruitsGameMode.BONUS_WHEEL) && SoundManager.playBigWinInitial();
        }, 0.528);
        this._tl.add(() => {

        }, 1);
        return this._tl;
    }

    /**
     * @inheritDoc
     * @param isActive
     * @param data
     */
    public abort(isActive: boolean, data: ParsedServerData): gsap.SimpleTimeline | gsap.TimelineLite | gsap.TimelineMax | null {
        const tl: TimelineLite = new TimelineLite();

        // #320
        StageManager.getLayer(ImmortalFruitsGameConfig.instance.LAYERS.INITIAL_WIN_AMOUNT_BET_LINES.name).removeChildren();

        tl.add(() => EventHandler.dispatchEvent(new GameEvent(BetLineEvent.HIDE_ALL_BET_LINES)), 0);
        tl.add(() => ImmortalFruitsGameModuleConfig.symWinAnims.stopAllAnimation(), 0);
        tl.add(() => {
            for (let reelId = 0; reelId < ImmortalFruitsGameConfig.instance.REELS_NUM; reelId++) {
                const reelCoords: [number, number][] = this._symbolStates.getCoordinatesWithStateInReel(reelId, ImmortalFruitsGameConfig.instance.SYMBOL_STATES.win!.keyword);
                for (let coords of reelCoords) {
                    this.changeStateOfSymbols(coords[0], coords[1], ImmortalFruitsGameConfig.instance.SYMBOL_STATES.normal!);
                }
            }
        }, 0);
        tl.add(this.getNoWinSymbolAnimations(), 0);

        return tl;
    }

    /**
     * @description This method will set the no win state.
     */
    private getNoWinSymbolAnimations(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        tl.add(() => {
            for (let reelId = 0; reelId < ImmortalFruitsGameConfig.instance.REELS_NUM; reelId++) {
                const reelCoords: [number, number][] = this._symbolStates.getCoordinatesWithStateInReel(reelId, ImmortalFruitsGameConfig.instance.SYMBOL_STATES.noWin!.keyword);
                for (let coords of reelCoords) {
                    this.changeStateOfSymbols(coords[0], coords[1], ImmortalFruitsGameConfig.instance.SYMBOL_STATES.noWin!);
                }
            }
        }, 0);

        return tl;
    }

    /**
     * @description This method will set the win state.
     */
    private getWinSymbolAnimations(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this.isNewRoundStarted = false;
        for (let reelId = 0; reelId < ImmortalFruitsGameConfig.instance.REELS_NUM; reelId++) {
            const reelCoords: [number, number][] = this._symbolStates.getCoordinatesWithStateInReel(reelId, ImmortalFruitsGameConfig.instance.SYMBOL_STATES.win!.keyword);
            const reelTL: TimelineLite = new TimelineLite();
            for (let coords of reelCoords) {
                reelTL.add(this.getSymbolAnimation(coords[0], coords[1], ImmortalFruitsGameConfig.instance.SYMBOL_STATES.win), 0);
                if (!ImmortalFruitsGameConfig.instance.WIN_BELOW_STAKE) {// #305
                    reelTL.add(ForegroundAnimation!.getSymbolAnimShow(coords[0], coords[1]), 0);
                    reelTL.add(() => {
                        !this.isNewRoundStarted && ForegroundAnimation!.getStaticBgShow(coords[0], coords[1])
                    }, 1);
                }

            }
            tl.add(reelTL, 0.1);
        }

        return tl;
    }

    /**
     * This method sorts the symbols.
     * @param symbol
     */
    private sortSymbolDepthToTop(symbol: StateReelSymbol): void {
        if (symbol && symbol.parent) {
            symbol.parent.addChild(symbol);
        }
    }

    /**
     * @description This method will set the provided state of the provided symbol of any provided reel.
     * @param reelId
     * @param symbolId
     * @param state
     */
    private getSymbolAnimation(reelId: number, symbolId: number, state: ISymbolStateConfig): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const symbol: StateReelSymbol | null = Reels.getSymbol(reelId, symbolId);
        if (symbol) {
            const symbolAnimation: Timeline = symbol.changeState({
                state: state,
                setStack: true,
                fadeInDuration: 0.0
            })!;

            tl.add([
                () => this.sortSymbolDepthToTop(symbol),
                symbolAnimation
            ]);
        }
        tl.add(() => {
        }, 1);
        return tl;
    }

    /**
     * @description To change the state of symbols.
     * @param reelId
     * @param symbolId
     * @param state
     */
    private changeStateOfSymbols(reelId: number, symbolId: number, state: ISymbolStateConfig): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const symbol: StateReelSymbol | null = Reels.getSymbol(reelId, symbolId);
        if (symbol!.symName == "S" && this._gameData.nextMode === ImmortalFruitsGameMode.BONUS_WHEEL) {
            return tl
        }
        if (symbol) {
            const symbolAnimation: Timeline = symbol.changeState({
                state: state,
                setStack: true,
                fadeInDuration: 0.0
            })!;

            tl.add([
                () => this.sortSymbolDepthToTop(symbol),
                symbolAnimation,
                () => {
                    if (state.keyword === "noWin") {
                        //this.noWinForSGlowRune();
                    }
                }
            ]);
        }

        return tl;
    }

    private addEventListeners() {
        EventHandler.addEventListener(this, BalanceEvent.BET, () => {
            this._tl?.kill();
            this.isNewRoundStarted = true;
        });
    }
}